import styled from '@emotion/styled'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '../../../api/graphql'
import BusinessCalendarCTA from './BusinessCalendarCTA'
import { useChatContext } from './ChatProvider'
import { MESSAGE_CREATE_MUTATION, SUPPORT_CONVERSATION_QUERY } from './chatQueries'
import MessageInput from './MessageInput'
import ReferralForm from './ReferralForm'
import { formatRelativeTime } from './timeUtils'

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  align-self: ${props => (props.isUser ? 'flex-end' : 'flex-start')};
  background: ${props => (props.isUser ? '#102638' : '#f3f4f6')};
  color: ${props => (props.isUser ? 'white' : '#111827')};
  word-wrap: break-word;
  white-space: pre-wrap;
`

const ClosingMessageContainer = styled.div`
  max-width: 92%;
  align-self: flex-start;
`

const MessageMeta = styled.div<{ isUser: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  align-self: ${props => (props.isUser ? 'flex-end' : 'flex-start')};
`

const SenderLabel = styled.span`
  font-weight: 500;
  color: #6b7280;
`

const StatusBar = styled.div`
  padding: 8px 16px;
  background: #fef3c7;
  text-align: center;
  font-size: 13px;
  color: #92400e;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const ReopenLink = styled.button`
  background: none;
  border: none;
  color: #102638;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
`

type ReferralSubmission = {
  id: string
  submittedAt: string
  consentToAttribute: boolean
  leadCount: number
}

type Message = {
  id: string
  body: string
  senderType: string
  senderName: string | null
  createdAt: string
  aiConfidence: number | null
  closingVariant: string | null
  referralSubmission: ReferralSubmission | null
}

type ConversationViewProps = {
  conversationId: string
}

const CLOSING_INCLUDES_FORM = ['spread_the_word', 'combined']
const CLOSING_INCLUDES_BC = ['business_calendar', 'combined']

const ConversationView: React.FC<ConversationViewProps> = ({ conversationId }) => {
  const { refetchUnread } = useChatContext()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [sendingMessage, setSendingMessage] = useState(false)

  const { data, refetch } = useQuery(SUPPORT_CONVERSATION_QUERY, {
    variables: { id: conversationId },
    pollInterval: 10000,
  })

  const [createMessage] = useMutation(MESSAGE_CREATE_MUTATION)
  const conversation = data?.supportConversation
  const messages: Message[] = conversation?.messages ?? []
  const isClosed = conversation?.status === 'closed'

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const handleSend = useCallback(
    async (body: string) => {
      setSendingMessage(true)
      try {
        await createMessage({
          variables: {
            input: {
              conversationId,
              body,
              pageContext: JSON.stringify({
                path: window.location.pathname,
                url: window.location.href,
              }),
            },
          },
        })
        refetch()
        refetchUnread()
      } finally {
        setSendingMessage(false)
      }
    },
    [conversationId, createMessage, refetch, refetchUnread]
  )

  const senderLabel = (message: Message) => {
    if (message.senderType === 'ai') return 'CivicLiftBot'
    if (message.senderType === 'admin') return message.senderName || 'Support'
    return 'You'
  }

  const renderMessage = (message: Message) => {
    const isUser = message.senderType === 'user'
    const variant = message.closingVariant
    const showForm = variant && CLOSING_INCLUDES_FORM.includes(variant) && !message.referralSubmission
    const showBC = variant && CLOSING_INCLUDES_BC.includes(variant)

    if (variant) {
      return (
        <React.Fragment key={message.id}>
          <ClosingMessageContainer>
            <MessageBubble isUser={false}>{message.body}</MessageBubble>
            {showBC && <BusinessCalendarCTA />}
            {showForm && <ReferralForm supportMessageId={message.id} onSubmitted={refetch} />}
          </ClosingMessageContainer>
          <MessageMeta isUser={false}>
            <SenderLabel>{senderLabel(message)}</SenderLabel>
            <span>{formatRelativeTime(message.createdAt)}</span>
          </MessageMeta>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment key={message.id}>
        <MessageBubble isUser={isUser}>{message.body}</MessageBubble>
        <MessageMeta isUser={isUser}>
          <SenderLabel>{senderLabel(message)}</SenderLabel>
          <span>{formatRelativeTime(message.createdAt)}</span>
        </MessageMeta>
      </React.Fragment>
    )
  }

  return (
    <Container>
      <MessagesArea>
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </MessagesArea>

      {isClosed && (
        <StatusBar>
          This conversation is closed.
          <ReopenLink onClick={() => handleSend('Reopening this conversation.')}>Reopen</ReopenLink>
        </StatusBar>
      )}

      {!isClosed && <MessageInput onSend={handleSend} disabled={sendingMessage} />}
    </Container>
  )
}

export default ConversationView
